import React from 'react';
import { Eye, Settings } from 'react-feather';
import { SortIcon } from '../InlineTable';
import { ToolbarButton, ConfigEmptyState } from '../ConfigPages';
import { RunStatePill } from '../EtlPages';
import { FileTypeBadge, formatTimestamp, Pagination } from './FileTrackingShared';

/**
 * Display status for a row.
 *
 * Trusts the backend: `manualReview.required` is only set when the flag's
 * effectiveDate matches this record's date, so re-deriving it here would be
 * redundant — and the version that did so didn't compare dates, letting a
 * stale flag from an earlier date override today's correctly-processed file.
 */
const displayStatus = (record) => ({
  needs_review: 'needs_review',
  processed: 'processed',
  received: 'pending',
  pending: 'pending',
  missed: 'missed',
  failed: 'failed',
}[record.status] || 'pending');

const COLUMNS = [
  { key: 'locationName', label: 'Location', sortable: true },
  { key: 'fileType', label: 'Feed', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'receivedAt', label: 'Received', sortable: true },
  { key: 'processedAt', label: 'Processed', sortable: false },
];

/**
 * The files table — one row per (location, feed) for the effective date.
 *
 * Rebuilt on the InlineTable kit: it was a CSS-grid pretending to be a table,
 * with hardcoded `2fr 1fr 1.2fr…` track widths repeated in the header and every
 * row, so the two drifted apart whenever a column changed.
 */
export default function FilesTable({
  records,
  selectedRecord,
  onSelectRecord,
  onViewFile,
  onConfigure,
  sortConfig,
  onSort,
  currentPage,
  itemsPerPage,
  onPageChange,
}) {
  const totalItems = records.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const start = (currentPage - 1) * itemsPerPage;
  const page = records.slice(start, start + itemsPerPage);

  const isSelected = (record) => (
    selectedRecord?.id === record.id
    || (selectedRecord?.hotelId === record.hotelId && selectedRecord?.fileType === record.fileType)
  );

  return (
    <div className="it-frame">
      <div className="it-scroll" style={{ '--it-max-h': '58vh' }}>
        <table className="it-tbl">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={col.sortable ? 'it-sort' : undefined}
                  onClick={col.sortable ? () => onSort(col.key) : undefined}
                  aria-sort={sortConfig?.key === col.key ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {col.label}
                    {col.sortable ? <SortIcon columnKey={col.key} sortKey={sortConfig?.key} direction={sortConfig?.direction} /> : null}
                  </span>
                </th>
              ))}
              <th aria-label="Actions" style={{ width: 84 }} />
            </tr>
          </thead>
          <tbody>
            {page.map((record, index) => (
              <tr
                key={record.id || `${record.hotelId}-${record.fileType}-${index}`}
                className={`cp-row it-row-hover${isSelected(record) ? ' it-row-selected' : ''}`}
                onClick={() => onSelectRecord(record)}
                style={{ cursor: 'pointer' }}
              >
                <td className="cp-cell">
                  <div className="cp-name">{record.metadata?.locationName || `Hotel ${record.hotelId}`}</div>
                  <div className="cp-code">{record.hotelId} · {record.auditCode || record.fileType}</div>
                </td>
                <td className="cp-cell">
                  <FileTypeBadge fileType={record.displayName || record.fileType || 'File'} />
                </td>
                <td className="cp-cell">
                  <RunStatePill
                    status={displayStatus(record)}
                    title={record.dlq?.lastErrorMessage || undefined}
                  />
                </td>
                <td className="cp-cell">
                  <span className="cp-mono">{formatTimestamp(record.reception?.receivedAt || record.tracking?.lastReceived)}</span>
                </td>
                <td className="cp-cell">
                  <span className="cp-mono">{formatTimestamp(record.processing?.completedAt || record.tracking?.lastSuccessfullyProcessed)}</span>
                </td>
                <td className="cp-cell-tight" style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                  <span className="cp-row-actions">
                    {(record.reception?.blobUrl || record.tracking?.lastProcessedFile) ? (
                      <ToolbarButton
                        small iconOnly variant="ghost" icon={Eye}
                        title="View the source file"
                        aria-label={`View file for ${record.fileType}`}
                        onClick={() => onViewFile(record)}
                      />
                    ) : null}
                    <ToolbarButton
                      small iconOnly variant="ghost" icon={Settings}
                      title="Alert schedule and recipients"
                      aria-label={`Tracking options for ${record.fileType}`}
                      onClick={() => onConfigure(record)}
                    />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!records.length ? (
          <ConfigEmptyState
            title="No files match these filters"
            body="Clear the search, the status tile or the location filter to see everything tracked today."
          />
        ) : null}
      </div>

      {totalItems > itemsPerPage ? (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      ) : null}
    </div>
  );
}
